import React, { useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Container from '@material-ui/core/Container'
import FilledInput from '@material-ui/core/FilledInput'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import { NotificationManager } from 'react-notifications'
import { Auth } from 'aws-amplify'
import PhoneInput from 'components/general/PhoneInput'
import Loader from 'components/general/Loader'
import GuestAPI from 'config/api/GuestAPI'
import { onError } from 'config/lib/errorLib'
import AuthHeader from 'components/argon/Headers/AuthHeader.js'
import componentStyles from 'assets/theme/views/auth/login.js'
import componentStylesButtons from 'assets/theme/components/button.js'
import { formatDomain } from 'config/helpers/Helpers'

import eeLogo from 'resources/images/ee-logo.png'

const useStyles = makeStyles(componentStyles)
const useStylesButtons = makeStyles(componentStylesButtons)

function Register() {
    const history = useHistory()
    const classes = { ...useStyles(), ...useStylesButtons() }
    const theme = useTheme()
    const viewArea = useRef(null)
    const api = new GuestAPI()

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [companyUrl, setCompanyUrl] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [confirmationCode, setConfirmationCode] = useState('')
    const [stage, setStage] = useState('register')
    const [loading, setLoading] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})

    const handleSubmit = (e) => {
        e.preventDefault()

        if (stage === 'register') {
            register()
        }
        else if (stage === 'confirmation') {
            confirmAccount()
        }
    }

    async function register() {
        setLoading(true)
        setFieldErrors({}) // Reset errors

        let formattedEmail = email.trim().toLowerCase()
        let formattedDomain = formatDomain(companyUrl)
        let errors = {}

        // Front-end validation
        if (!firstName) errors.firstName = 'First name is required'
        if (!lastName) errors.lastName = 'Last name is required'
        if (!formattedEmail.match(/^[^@]+@[^@]+\.[^@]+$/)) errors.email = 'Enter a valid email address'
        if (!phone) errors.phone = 'Phone number is required'
        if (!companyName) errors.companyName = 'Company name is required'
        if (!companyUrl) errors.companyUrl = 'Company website is required'
        if (!password || password.length < 8) errors.password = 'Password must be at least 8 characters'
        if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match'

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            setLoading(false)
            return
        }

        let cognitoData = {
            'username': formattedEmail,
            'password': password,
            'attributes': {
                'phone_number': phone,
                'custom:companyName': companyName,
                'custom:companyUrl': formattedDomain
            }
        }

        try {
            // check if company exists
            const exists = await api.checkCompany(formattedDomain)
            if (exists) {
                setFieldErrors({ companyUrl: 'This company has already been registered. Please contact colm@energyefficiency.ie for further details.' })
                setLoading(false)
            }
            else {
                // register user
                await Auth.signUp(cognitoData)
                // add details to companies table in Dynamo
                await api.addCompany(formattedDomain, companyName, firstName, lastName, formattedEmail, phone)
                // sign in
                signIn()
            }
        } catch(e) {
            let errors = {}
            if (e.code === 'UsernameExistsException') {
                errors.email = 'An account has already been registered with this email. Please log in.'
                setStage('login')
            }
            else if (e.code === 'InvalidPasswordException') {
                errors.password = 'Password does not meet requirements (minimum 8 characters)'
            }
            else if (e.code === 'InvalidParameterException') {
                errors.email = 'Please ensure you have entered a valid email address (john@gmail.com)'
            }
            else {
                errors.general = e.message || 'An error occurred. Please try again.'
                onError(e)
            }
            setFieldErrors(errors)
            setLoading(false)
        }
    }

    async function signIn() {
        setLoading(true)
        let formattedEmail = email.trim().toLowerCase()
        try {
            const user = await Auth.signIn(formattedEmail, password)
            window.location.reload()
        } catch(e) {
            let errors = {}
            if (e.code === 'UserNotConfirmedException') {
                setStage('confirmation')
            }
            else if (e.code === 'UserNotFoundException') {
                errors.email = 'An account with this email has not yet been registered.'
            }
            else if (e.code === 'NotAuthorizedException') {
                errors.password = 'The email or password you have entered is incorrect'
            }
            else {
                errors.general = e.message || 'An error occurred. Please try again.'
                onError(e)
            }
            setFieldErrors(errors)
            setLoading(false)
        }
    }

    async function confirmAccount() {
        setLoading(true)
        let formattedEmail = email.trim().toLowerCase()
        if (confirmationCode.length > 0) {
            try {
                await Auth.confirmSignUp(formattedEmail, confirmationCode)
                signIn()
            } catch(e) {
                let errors = {}
                if (e.code === 'CodeMismatchException') {
                    errors.confirmationCode = 'Invalid code, please ensure you have entered the correct code'
                } else {
                    errors.general = e.message || 'An error occurred. Please try again.'
                }
                setFieldErrors(errors)
                onError(e)
            }
        }
        else {
            setFieldErrors({ confirmationCode: 'Please enter the code that was sent to ' + formattedEmail })
        }
        setLoading(false)
    }

    async function resendCode() {
        let formattedEmail = email.trim().toLowerCase()
        try {
            await Auth.resendSignUp(formattedEmail)
            NotificationManager.success('Confirmation code sent to ' + formattedEmail)
        } catch(e) {
            onError(e)
        }
    }

    return (
        <>
            <Grid container direction='row' justifyContent='center' alignItems='center' style={{width: '100%', padding: '1rem 3rem'}}>
                <Grid item xs={12} md={6} className='login-logo-container'>
                    <img className='login-logo' src={eeLogo} alt='logo' onClick={() => history.push('/login')} />
                </Grid>
                <Grid item xs={12} md={6}></Grid>
            </Grid>

            <Box ref={viewArea}></Box>

            {stage === 'register' &&
                <AuthHeader
                    title='Provider Registration'
                    description='Enter your details to get started'
                />
            }
            {stage === 'confirmation' &&
                <AuthHeader
                    title='Check your email'
                    description={'We just sent a confirmation code via email to ' + email}
                />
            }

            <Container
                component={Box}
                maxWidth='xl'
                marginTop='-12rem'
                paddingBottom='3rem'
                position='relative'
                zIndex='101'
            >
                {stage === 'register' &&
                    <form onSubmit={handleSubmit}>
                        <Box component={Grid} container justifyContent='center'>
                            <Grid item xs={12} md={7}>
                                <Card classes={{ root: classes.cardRoot }} style={{backgroundColor: '#fff'}}>
                                    <CardContent classes={{ root: classes.cardContent }}>
                                        {fieldErrors.general && (
                                            <Box color="error.main" textAlign="center" mb={2}>{fieldErrors.general}</Box>
                                        )}
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={6}>
                                                <FormGroup>
                                                    <FormLabel>First Name</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='text'
                                                            placeholder='John'
                                                            value={firstName}
                                                            onChange={(e) => setFirstName(e.target.value)}
                                                        />
                                                        {fieldErrors.firstName && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.firstName}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormGroup>
                                                    <FormLabel>Last Name</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='text'
                                                            placeholder='Smith'
                                                            value={lastName}
                                                            onChange={(e) => setLastName(e.target.value)}
                                                        />
                                                        {fieldErrors.lastName && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.lastName}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Email</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='text'
                                                            placeholder='john.smith@gmail.com'
                                                            value={email}
                                                            onChange={(e) => setEmail(e.target.value)}
                                                        />
                                                        {fieldErrors.email && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.email}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Phone Number</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <PhoneInput
                                                            phone={phone}
                                                            updatePhone={(phone) => setPhone(phone)}
                                                        />
                                                        {fieldErrors.phone && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.phone}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Company Name</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='text'
                                                            placeholder='My Company'
                                                            value={companyName}
                                                            onChange={(e) => setCompanyName(e.target.value)}
                                                        />
                                                        {fieldErrors.companyName && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.companyName}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Company Website URL</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='text'
                                                            placeholder='mycompany.ie'
                                                            value={companyUrl}
                                                            onChange={(e) => setCompanyUrl(e.target.value)}
                                                        />
                                                        {fieldErrors.companyUrl && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.companyUrl}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Password</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='password'
                                                            placeholder='Password'
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                        />
                                                        {fieldErrors.password && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.password}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{marginBottom: '1.5rem'}}>
                                            <Grid item xs={12}>
                                                <FormGroup>
                                                    <FormLabel>Confirm Password</FormLabel>
                                                    <FormControl
                                                        variant='filled'
                                                        component={Box}
                                                        width='100%'
                                                    >
                                                        <FilledInput
                                                            autoComplete='off'
                                                            type='password'
                                                            placeholder='Confirm Password'
                                                            value={confirmPassword}
                                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                                        />
                                                        {fieldErrors.confirmPassword && (
                                                            <Box color="error.main" fontSize="0.85rem" mt="0.5rem">{fieldErrors.confirmPassword}</Box>
                                                        )}
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>

                                        <Box
                                            textAlign='center'
                                            marginTop='1.5rem'
                                            marginBottom='1.5rem'
                                        >
                                            <Button
                                                variant='contained'
                                                color='primary'
                                                type='submit'
                                                disabled={loading}
                                            >
                                                {!loading ? (
                                                    <>Next</>
                                                ) : (
                                                    <Loader height={12} />
                                                )}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Grid container component={Box} marginTop='1rem'>
                                    <Grid item xs={12} component={Box} textAlign='center'>
                                        <p
                                            onClick={(e) => history.push('/login')}
                                            className='login-prompt'
                                        >
                                            Already have a profile? Log in now
                                        </p>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </form>
                }
                {stage === 'confirmation' &&
                    <form onSubmit={handleSubmit}>
                        <Box component={Grid} container justifyContent='center'>
                            <Grid item xs={12} lg={6} md={8}>
                                <Card classes={{ root: classes.cardRoot }} style={{backgroundColor: '#fff'}}>
                                    <CardContent classes={{ root: classes.cardContent }}>
                                        {fieldErrors.general && (
                                            <Box color="error.main" textAlign="center" mb={2}>{fieldErrors.general}</Box>
                                        )}
                                        <Box
                                            color={theme.palette.gray[600]}
                                            textAlign='center'
                                            marginBottom='1rem'
                                            marginTop='.5rem'
                                            fontSize='1rem'
                                        >
                                            <Box fontSize='80%' fontWeight='400' component='small'>
                                                Enter your confirmation code below
                                            </Box>
                                        </Box>

                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                            marginBottom='1rem!important'
                                        >
                                            <FilledInput
                                                autoComplete='off'
                                                type='email'
                                                placeholder='Email'
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled
                                            />
                                        </FormControl>

                                        <FormControl
                                            variant='filled'
                                            component={Box}
                                            width='100%'
                                            marginBottom='1rem!important'
                                        >
                                            <FilledInput
                                                autoComplete='off'
                                                type='text'
                                                placeholder='Confirmation Code'
                                                value={confirmationCode}
                                                onChange={(e) => setConfirmationCode(e.target.value)}
                                            />
                                            {fieldErrors.confirmationCode && (
                                                <Box color="error.main" fontSize="0.85rem">{fieldErrors.confirmationCode}</Box>
                                            )}
                                        </FormControl>

                                        <Box
                                            textAlign='center'
                                            marginTop='1.5rem'
                                            marginBottom='1.5rem'
                                        >
                                            <Button
                                                variant='contained'
                                                color='secondary'
                                                onClick={() => resendCode()}
                                                style={{marginRight: '1rem'}}
                                            >
                                                Resend Code
                                            </Button>
                                            <Button
                                                variant='contained'
                                                color='primary'
                                                type='submit'
                                                disabled={loading}
                                            >
                                                {!loading ? (
                                                    <>Confirm Account</>
                                                ) : (
                                                    <Loader height={12} />
                                                )}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Box>
                    </form>
                }
            </Container>
        </>
    )
}

export default Register